/**
 * One supplier's offer on one item (prototype `vCompare`'s `.scell`).
 *
 * Click to source from this supplier; click again to drop them. When selected, a quantity
 * input appears so the line can be split across several suppliers — the whole point of the
 * workbench.
 *
 * Shows the rate as quoted, plus its base-currency equivalent when the supplier quoted in
 * another currency, and again per RFQ UOM when they quoted in another unit. The purchaser
 * needs to see both what was written and what it means.
 */

import { Badge } from '../../components/Badge'
import { remarkUomConflict } from '../../lib/remarkUom'
import { Money } from '../../components/Money'
import { formatNumber } from '../../lib/format'
import type { NormalisedOffer } from '../../lib/compare'
import type { DisplayCurrency } from '../../lib/displayCurrency'
import type { Currency } from '../../lib/types'

export function OfferCell({
  offer,
  requiredQty,
  allocatedQty,
  isBest,
  baseCurrency,
  display,
  onToggle,
  onSetQty,
}: {
  offer: NormalisedOffer | undefined
  requiredQty: number
  allocatedQty: number
  isBest: boolean
  baseCurrency: Currency
  /** The currency the customer is quoted in — the comparable figure is shown in it too. */
  display?: DisplayCurrency
  onToggle: () => void
  onSetQty: (qty: number) => void
}) {
  // No offer from this supplier on this item.
  if (!offer) {
    return (
      <td>
        <div style={{ textAlign: 'center', color: 'var(--muted)' }}>—</div>
      </td>
    )
  }

  const selected = allocatedQty > 0
  const quotedCurrency = offer.raw.rate.currency
  // A unit named in the remark that contradicts the line's own — see where it renders below.
  const remarkUnitConflict = remarkUomConflict(offer.raw.remark, offer.raw.rfq_uom)
  const partial = offer.stock < requiredQty

  return (
    <td>
      <div
        className={`scell${isBest ? ' best' : ''}${selected ? ' sel' : ''}`}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        <span className="rate">
          <Money value={offer.raw.rate} />
          {offer.converted ? <span className="sub">/{offer.raw.sq_uom}</span> : null}
          {/* The comparable number: base currency, per the unit the RFQ asked for. */}
          {quotedCurrency !== baseCurrency || offer.converted ? (
            <span className="sub">
              {' '}
              = <Money value={{ amount: offer.unitInBase, currency: baseCurrency }} />
              {offer.converted ? `/${offer.raw.rfq_uom}` : ''}
            </span>
          ) : null}
          {/* In the customer's currency, when that differs — what this offer will be quoted at. */}
          {display && display.currency !== baseCurrency ? (
            <span className="sub" title={`In ${display.currency}, the currency this customer is quoted in`}>
              {' '}
              → <Money value={{ amount: offer.unitInBase, currency: baseCurrency }} in={display.currency} fx={display.fx} />
            </span>
          ) : null}
        </span>

        <span className="sub">
          stock {formatNumber(offer.stock, 0)} {offer.raw.rfq_uom}{' '}
          {/*
            "partial" stays — an offer covering less than the line is the one thing to see here.
            The "full" badge is gone (Akhil, 2026-09-18): suppliers mostly do not edit the quantity
            on the RFQ workbook, so every cell wore it and it said nothing.
          */}
          {partial ? <Badge tone="warn">partial</Badge> : null}
        </span>

        {offer.converted ? (
          <span className="sub">
            {/* Stated in whichever direction reads as a whole number: "1 box = 24 pcs"
                rather than "1 pcs = 0.0416 box". */}
            <Badge tone="warn">
              {offer.factor >= 1
                ? `1 ${offer.raw.sq_uom} = ${formatNumber(offer.factor, 2)} ${offer.raw.rfq_uom}`
                : `1 ${offer.raw.rfq_uom} = ${formatNumber(1 / offer.factor, 2)} ${offer.raw.sq_uom}`}
            </Badge>
          </span>
        ) : null}

        {/* The supplier's own note on this line — lead time, MOQ, "FOB", "ex-stock Dubai".
            It routinely decides the allocation, so it belongs beside the price rather than
            back on the supplier-quotations screen. */}
        {/*
          Point 21 — a colour of its own, not the muted grey every other sub-line uses.

          Teal, and specifically NOT `--warn`: a remark is the supplier telling us something
          ("ex-stock Dubai", "lead time 3 days", "price is per inch"), not a problem with the line.
          Warn is already spoken for on this screen by the UOM mismatch and the price-outlier
          flag, and a note that shouts as loudly as those trains people to ignore all three.
        */}
        {/*
          A unit named in the remark that contradicts the line's own.

          The UOM here is the RFQ's and is not editable, so a supplier who priced against a
          different unit can only say so in prose — and on this bench two of nineteen remarks do
          ("costing is based inches" on a MTR line; "Per PCS" on a DOZ line). Those are ~39x and
          12x discrepancies hiding inside a note styled as an aside.

          Warn-toned, unlike the remark below it: this one IS a problem with the line. It flags
          rather than excludes — the remark is the supplier's claim, not a fact about the
          quotation, and the purchaser is the one who can read it and decide.
        */}
        {remarkUnitConflict ? (
          <span
            className="sub"
            style={{ whiteSpace: 'normal', color: 'var(--warn)', fontWeight: 600 }}
            title={`The remark prices this in ${remarkUnitConflict}, but the line is in ${offer.raw.rfq_uom}. Check before allocating.`}
          >
            ⚠ remark says {remarkUnitConflict} — line is {offer.raw.rfq_uom}
          </span>
        ) : null}

        {offer.raw.remark ? (
          <span
            className="sub"
            style={{ whiteSpace: 'normal', fontStyle: 'italic', color: 'var(--teal)' }}
            title={offer.raw.remark}
          >
            “{offer.raw.remark}”
          </span>
        ) : null}

        {selected ? (
          <input
            type="number"
            value={allocatedQty}
            aria-label={`Quantity from ${offer.supplier}`}
            // The input lives inside the clickable cell; typing must not toggle it off.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={(e) => onSetQty(Math.max(0, Number(e.target.value) || 0))}
          />
        ) : null}
      </div>
    </td>
  )
}
